import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { inr, ymd } from "@/lib/format";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading } from "@/components/pawkt/Empty";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TrendingUp, TrendingDown, AlertTriangle, Info, ArrowRight, Landmark, Wrench } from "lucide-react";

const fmtPct = (v) => (v === null || v === undefined ? "—" : `${v > 0 ? "+" : ""}${Number(v).toFixed(1)}%`);
const monthStr = (d) => ymd(d);
const firstOfMonth = (dt) => new Date(dt.getFullYear(), dt.getMonth(), 1);
const lastOfMonth = (dt) => new Date(dt.getFullYear(), dt.getMonth() + 1, 0);

function Delta({ d, invert }) {
  if (d === null || d === undefined) return null;
  const up = d > 0;
  const good = invert ? !up : up;
  const Icon = up ? TrendingUp : TrendingDown;
  return <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${good ? "text-emerald-600" : "text-rose-600"}`}><Icon className="h-3 w-3" />{fmtPct(d)}</span>;
}

function KpiCard({ label, kpi, isPct, invert, testid, primary }) {
  const val = isPct ? `${Number(kpi.current || 0).toFixed(1)}%` : inr(kpi.current || 0);
  return (
    <div className={`rounded-xl border p-4 ${primary ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50"}`} data-testid={testid}>
      <div className="text-xs font-medium text-slate-500">{label}</div>
      <div className={`mt-1 font-display font-extrabold text-slate-900 ${primary ? "text-2xl" : "text-xl"}`}>{val}</div>
      <div className="mt-1.5 flex items-center justify-between gap-2">
        {kpi.percentage_change !== undefined ? <Delta d={kpi.percentage_change} invert={invert} /> : <span />}
        {kpi.plan !== undefined && kpi.plan !== null && <span className="text-[11px] text-slate-400">Plan {isPct ? "" : inr(kpi.plan)}{kpi.plan_pct ? ` · ${kpi.plan_pct.toFixed(0)}%` : ""}</span>}
      </div>
    </div>
  );
}

function Section({ title, subtitle, children, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
      <h3 className="font-display text-lg font-semibold text-slate-900">{title}</h3>
      {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
      <div className="mt-4">{children}</div>
    </div>
  );
}

export default function BusinessReview() {
  const navigate = useNavigate();
  const now = new Date();
  const [range, setRange] = useState({ mode: "current", start: monthStr(firstOfMonth(now)), end: monthStr(lastOfMonth(now)) });

  const setPreset = (mode) => {
    if (mode === "current") setRange({ mode, start: monthStr(firstOfMonth(now)), end: monthStr(lastOfMonth(now)) });
    else if (mode === "previous") { const pm = new Date(now.getFullYear(), now.getMonth() - 1, 1); setRange({ mode, start: monthStr(firstOfMonth(pm)), end: monthStr(lastOfMonth(pm)) }); }
    else setRange((r) => ({ ...r, mode }));
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["monthly-review", range.start, range.end],
    queryFn: async () => (await api.get(`/profit-intelligence/monthly-review?period_start=${range.start}&period_end=${range.end}`)).data,
  });
  const { data: actionsData } = useQuery({
    queryKey: ["br-top-actions", range.start, range.end],
    queryFn: async () => (await api.get(`/business-actions?period_start=${range.start}&period_end=${range.end}`)).data,
  });
  const topActions = (actionsData?.actions || []).slice(0, 5);

  const d = data || {};
  const k = d.kpis || {};
  const pa = d.plan_vs_actual || {};

  return (
    <div className="space-y-6">
      <PageHeader title="Monthly Business Review" subtitle="How is my business actually doing? A management view over Profit Intelligence, Business Planning and your recorded costs.">
        <div className="flex flex-wrap items-center gap-1.5">
          <Button size="sm" variant={range.mode === "current" ? "default" : "outline"} onClick={() => setPreset("current")} data-testid="br-preset-current">This Month</Button>
          <Button size="sm" variant={range.mode === "previous" ? "default" : "outline"} onClick={() => setPreset("previous")} data-testid="br-preset-previous">Last Month</Button>
          <Button size="sm" variant={range.mode === "custom" ? "default" : "outline"} onClick={() => setPreset("custom")} data-testid="br-preset-custom">Custom</Button>
        </div>
      </PageHeader>

      {range.mode === "custom" && (
        <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid="br-custom-range">
          <div><div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">From</div><Input type="date" value={range.start} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))} data-testid="br-custom-from" className="h-9 bg-white" /></div>
          <div><div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">To</div><Input type="date" value={range.end} onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))} data-testid="br-custom-to" className="h-9 bg-white" /></div>
        </div>
      )}

      {isLoading ? <Loading /> : isError ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-700" data-testid="br-error">
          {error?.response?.status === 403 ? "Business Review requires Profit Intelligence to be enabled for your account." : "Unable to load the business review."}
        </div>
      ) : (
        <>
          <div className="text-xs text-slate-400" data-testid="br-period">{d.period?.label} · vs {d.comparison_period?.label} ({d.comparison_period?.basis?.replace(/_/g, " ")})</div>

          {/* KPI HEADER */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="br-kpis">
            <KpiCard primary label="Revenue" kpi={k.revenue || {}} testid="br-kpi-revenue" />
            <KpiCard primary label="Operating Cost" kpi={k.operating_cost || {}} invert testid="br-kpi-opcost" />
            <KpiCard primary label="Operating Result" kpi={k.operating_result || {}} testid="br-kpi-opresult" />
            <KpiCard primary label="Operating Margin" kpi={k.operating_margin || {}} isPct testid="br-kpi-margin" />
          </div>
          <div className="grid grid-cols-2 gap-3 -mt-3" data-testid="br-secondary-kpis">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="br-kpi-financial">
              <div className="flex items-center gap-1.5 text-xs font-medium text-amber-700"><Landmark className="h-3.5 w-3.5" /> Financial Cost</div>
              <div className="mt-1 font-display text-xl font-extrabold text-amber-900">{inr(k.financial_cost?.current || 0)}</div>
              <div className="text-[11px] text-amber-600 mt-0.5">Part of operating cost, shown separately</div>
            </div>
            <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4" data-testid="br-kpi-capex">
              <div className="flex items-center gap-1.5 text-xs font-medium text-indigo-700"><Wrench className="h-3.5 w-3.5" /> Capital Expenditure</div>
              <div className="mt-1 font-display text-xl font-extrabold text-indigo-900">{inr(k.capital_expenditure?.current || 0)}</div>
              <div className="text-[11px] text-indigo-600 mt-0.5">NOT included in operating cost or result</div>
            </div>
          </div>

          {/* TOP ACTIONS */}
          {topActions.length > 0 && (
            <Section title="Top actions" subtitle="What deserves your attention — derived from this review" testid="br-top-actions">
              <div className="space-y-2">
                {topActions.map((a) => (
                  <button key={a.id} data-testid={`br-action-${a.id}`} onClick={() => navigate("/business-actions")}
                    className="flex w-full items-center gap-3 rounded-lg border border-slate-100 px-3 py-2 text-left hover:bg-slate-50">
                    <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${{ CRITICAL: "bg-rose-500", HIGH: "bg-orange-500", MEDIUM: "bg-amber-500", LOW: "bg-slate-400" }[a.priority]}`} />
                    <span className="flex-1 truncate text-sm font-medium text-slate-700">{a.title}</span>
                    {a.financial_impact?.available && <span className="shrink-0 text-xs font-semibold text-slate-500">{inr(a.financial_impact.amount)}</span>}
                  </button>
                ))}
              </div>
              <Button variant="outline" size="sm" className="mt-3 gap-1.5" data-testid="br-view-all-actions" onClick={() => navigate("/business-actions")}>View all business actions <ArrowRight className="h-4 w-4" /></Button>
            </Section>
          )}

          {/* MANAGEMENT SUMMARY */}
          {(d.management_summary?.points || []).length > 0 && (
            <Section title="Business health" subtitle="Generated from this period's actual data" testid="br-summary">
              <ul className="space-y-2">
                {d.management_summary.points.map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700" data-testid={`br-summary-point-${i}`}><ArrowRight className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />{p}</li>
                ))}
              </ul>
            </Section>
          )}

          {/* PLAN VS ACTUAL */}
          <Section title="Plan vs Actual" subtitle={pa.note} testid="br-plan-vs-actual">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[480px]">
                <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100"><th className="py-2">Metric</th><th className="py-2 text-right">Plan</th><th className="py-2 text-right">Actual</th><th className="py-2 text-right">Variance</th></tr></thead>
                <tbody>
                  {[["Revenue", "revenue", false], ["Operating Cost", "operating_cost", false], ["Operating Result", "operating_result", false], ["Operating Margin", "operating_margin", true]].map(([label, key, isPct]) => {
                    const m = pa.metrics?.[key] || {};
                    return (
                      <tr key={key} className="border-b border-slate-50" data-testid={`br-pa-${key}`}>
                        <td className="py-2.5 font-medium text-slate-700">{label}</td>
                        <td className="py-2.5 text-right text-slate-500">{m.status === "NO_PLAN" ? <span className="text-xs italic text-slate-400">No plan configured</span> : (isPct ? `${Number(m.plan || 0).toFixed(1)}%` : inr(m.plan || 0))}</td>
                        <td className="py-2.5 text-right font-semibold text-slate-900">{isPct ? `${Number(m.actual || 0).toFixed(1)}%` : inr(m.actual || 0)}</td>
                        <td className="py-2.5 text-right">{m.status === "NO_PLAN" ? "—" : <span className={m.variance >= 0 ? "text-emerald-600 font-semibold" : "text-rose-600 font-semibold"}>{m.variance_pct ? `${m.variance_pct.toFixed(0)}%` : inr(m.variance || 0)}</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>

          {/* WHERE DID MY MONEY GO */}
          <Section title="Where did my money go?" subtitle="Recorded cost by category — click to drill into the underlying expenses" testid="br-cost-breakdown">
            {(d.cost_breakdown || []).length === 0 ? <p className="text-sm text-slate-400">No costs recorded this period.</p> : (
              <div className="space-y-2">
                {d.cost_breakdown.map((c) => (
                  <button key={c.category} onClick={() => navigate("/finance")} data-testid={`br-cost-${c.category}`} className="w-full flex items-center gap-3 text-left rounded-lg hover:bg-slate-50 px-2 py-1.5">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between text-sm"><span className="font-medium text-slate-700 capitalize">{c.category.replace(/_/g, " ")}</span><span className="font-semibold text-slate-900">{inr(c.amount)}</span></div>
                      <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-slate-800" style={{ width: `${Math.min(100, c.share_pct || 0)}%` }} /></div>
                    </div>
                    <span className="text-[11px] text-slate-400 w-10 text-right">{(c.share_pct || 0).toFixed(0)}%</span>
                  </button>
                ))}
              </div>
            )}
          </Section>

          {/* TOP COST MOVERS */}
          {(d.top_cost_movers || []).length > 0 && (
            <Section title="Top cost movers" subtitle={`Material changes vs ${d.comparison_period?.label}`} testid="br-movers">
              <div className="flex flex-wrap gap-2">
                {d.top_cost_movers.map((m) => (
                  <div key={m.category} className={`rounded-lg border px-3 py-2 ${m.direction === "up" ? "border-rose-200 bg-rose-50" : "border-emerald-200 bg-emerald-50"}`} data-testid={`br-mover-${m.category}`}>
                    <div className="text-xs font-medium text-slate-600 capitalize">{m.category.replace(/_/g, " ")}</div>
                    <div className={`text-sm font-bold ${m.direction === "up" ? "text-rose-600" : "text-emerald-600"}`}>{fmtPct(m.percentage_change)} · {inr(Math.abs(m.absolute_change))}</div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* HIDDEN COST SIGNALS */}
          <Section title="Potential missing costs" subtitle="Review signals — never auto-recorded. The cost may simply be maintained outside Pawkt." testid="br-hidden-signals">
            {(d.hidden_cost_signals || []).length === 0 ? <p className="text-sm text-slate-400" data-testid="br-no-signals">No cost-review signals for this period.</p> : (
              <div className="space-y-2">
                {d.hidden_cost_signals.map((s, i) => (
                  <div key={i} className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid={`br-signal-${i}`}>
                    <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm text-amber-900">{s.message}</p>
                      {s.action && <Button size="sm" variant="outline" className="mt-2 h-8" onClick={() => navigate(s.action.type === "unit_economics" ? "/business-review" : "/finance")} data-testid={`br-signal-action-${i}`}>{s.action.label}</Button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* UNIT ECONOMICS */}
          <Section title="Unit economics" subtitle="Computed only when real operational volume and cost exist — never estimated" testid="br-unit-economics">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {(d.unit_economics || []).map((u) => (
                <div key={u.service} className="rounded-lg border border-slate-200 p-3" data-testid={`br-ue-${u.service}`}>
                  <div className="flex items-center justify-between"><span className="text-sm font-semibold text-slate-800 capitalize">{u.service}</span><span className="text-[10px] uppercase text-slate-400">per {u.unit}</span></div>
                  {u.status === "OK" ? (
                    <div className="mt-2 space-y-1 text-sm">
                      <div className="flex justify-between"><span className="text-slate-500">Revenue</span><span className="font-medium">{inr(u.revenue_per_unit)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Cost</span><span className="font-medium">{inr(u.cost_per_unit)}</span></div>
                      <div className="flex justify-between border-t border-slate-100 pt-1"><span className="text-slate-600 font-medium">Contribution</span><span className={`font-bold ${u.contribution_per_unit >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{inr(u.contribution_per_unit)}</span></div>
                      <div className="text-[11px] text-slate-400">{u.volume} {u.volume_basis}</div>
                    </div>
                  ) : (
                    <div className="mt-2 flex items-start gap-1.5 text-xs text-slate-400"><Info className="h-3.5 w-3.5 shrink-0 mt-0.5" /><span data-testid={`br-ue-insufficient-${u.service}`}>Insufficient data — {u.reason}.</span></div>
                  )}
                </div>
              ))}
            </div>
          </Section>

          {/* SERVICE PROFITABILITY */}
          <Section title="Service profitability" subtitle={d.service_profitability?.note} testid="br-service-profitability">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]">
                <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100"><th className="py-2">Service</th><th className="py-2 text-right">Revenue</th><th className="py-2 text-right">Direct Cost</th><th className="py-2 text-right">Contribution</th><th className="py-2 text-right">Margin</th></tr></thead>
                <tbody>
                  {(d.service_profitability?.rows || []).map((r) => (
                    <tr key={r.service} className="border-b border-slate-50" data-testid={`br-sp-${r.service}`}>
                      <td className="py-2.5 font-medium text-slate-700 capitalize">{r.service}</td>
                      <td className="py-2.5 text-right">{inr(r.revenue)}</td>
                      <td className="py-2.5 text-right">{r.cost_status === "COST_UNALLOCATED" ? <span className="text-xs italic text-slate-400">unallocated</span> : inr(r.direct_cost)}</td>
                      <td className="py-2.5 text-right font-semibold">{inr(r.contribution)}</td>
                      <td className="py-2.5 text-right">{r.contribution_margin_pct === null ? "—" : `${r.contribution_margin_pct.toFixed(0)}%`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {d.service_profitability?.unallocated_cost > 0 && (
              <p className="mt-3 text-xs text-slate-400" data-testid="br-sp-unallocated">Plus {inr(d.service_profitability.unallocated_cost)} of unallocated cost (COGS, commissions & unassigned expenses) not attributed to any service. Contribution is not net profit.</p>
            )}
          </Section>

          {/* DATA QUALITY */}
          {(d.data_quality?.limitations || []).length > 0 && (
            <Section title="Data quality & limitations" subtitle="What this review can and cannot confirm" testid="br-data-quality">
              <ul className="space-y-1.5">
                {d.data_quality.limitations.map((l, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-500" data-testid={`br-dq-${i}`}><Info className="h-3.5 w-3.5 text-slate-300 shrink-0 mt-0.5" />{l}</li>
                ))}
              </ul>
            </Section>
          )}
        </>
      )}
    </div>
  );
}
